import {
  actionCreatorsReports,
  EReportFileType,
  EReportPeriod,
  EReportRepeatType,
  generateTabletGeneratedId,
  ICustomReport,
  ICustomReportComponent,
  IDashboardLayoutElement,
  TReportsStoreData,
} from '@westondev/tableturn-core';
import { setIsLoadingModal, setShowToast } from '../app/actions';
import { CUSTOM_REPORT_NUMBER_OF_ROWS } from '@app/components/LoggedIn/Reports/CustomReports/AddEditCustomReports/CustomReportsCreator/types';
import { AppThunk } from '../store';
import { selectCurrentDashboard } from '../selectors/homeSelectors';
import router from '@app/router/router';
import { IDashboardFilters } from '@app/components/LoggedIn/Home/DashboardCreator/types';
import { getPathWithOrgData } from '@app/helpers/navigation';
import { isCustomReportElementInCorrectPage } from '@app/helpers/reports/reports';

export const addCustomReportElement = ({
  newElement,
  neWidget,
}: {
  newElement: IDashboardLayoutElement;
  neWidget: ICustomReportComponent;
}): AppThunk<void> => {
  return (dispatch, getState) => {
    const currentEditCustomReports = getState().reports.changeData
      .data as ICustomReport;
    if (!currentEditCustomReports) return;

    const newWidgetWidId: ICustomReportComponent = {
      ...neWidget,
      id: generateTabletGeneratedId(),
    };
    const elements = Object.values(currentEditCustomReports.layout);
    const lastElementId =
      elements.length > 0 ? Number(elements[elements.length - 1].id) : 0;

    const newElementWithId: any = {
      ...newElement,
      id: lastElementId + 1,
      widgetId: newWidgetWidId.id,
    };

    dispatch(
      actionCreatorsReports.handleReportsChange(currentData => ({
        ...currentData,
        layout: {
          ...currentEditCustomReports.layout,
          [newElementWithId.id]: newElementWithId,
        },
        customReportComponents: {
          ...currentEditCustomReports.customReportComponents,
          [newWidgetWidId.id]: newWidgetWidId,
        },
      })),
    );
  };
};

export const updateCustomReportElement = (
  elements: ICustomReport['layout'],
): AppThunk<void> => {
  return (dispatch, getState) => {
    const currentEditCustomReports = getState().reports.changeData
      .data as ICustomReport;
    if (!currentEditCustomReports) return;

    if (!currentEditCustomReports) return;

    const currentElements = currentEditCustomReports.layout;

    dispatch(
      actionCreatorsReports.handleReportsChange(currentData => ({
        ...currentData,
        layout: {
          ...currentElements,
          ...elements,
        },
      })),
    );
  };
};

export const removeCustomReportElement = (
  elementId: string,
): AppThunk<void> => {
  return (dispatch, getState) => {
    const currentEditCustomReport = getState().reports.changeData
      .data as ICustomReport;
    if (!currentEditCustomReport) return;

    const currentElement = currentEditCustomReport.layout[elementId];

    if (!currentElement) {
      return dispatch(
        setShowToast({
          title: 'Error',
          description: 'Error removing element. Please try again.',
          type: 'error',
        }),
      );
    }
    const newLayout = { ...currentEditCustomReport.layout };
    delete newLayout[elementId];

    const newWidgets = { ...currentEditCustomReport.customReportComponents };
    if (currentElement.widgetId.toString().includes('-')) {
      delete newWidgets[currentElement.widgetId];
    } else {
      newWidgets[currentElement.widgetId] = null;
    }

    dispatch(
      actionCreatorsReports.handleReportsChange(currentData => ({
        ...currentData,
        layout: newLayout,
        customReportComponents: newWidgets,
      })),
    );
  };
};

export const updateCustomReportComponents = (
  widget: ICustomReportComponent,
): AppThunk<void> => {
  return (dispatch, getState) => {
    const currentEditDashboard = getState().reports.changeData
      .data as ICustomReport;
    if (!currentEditDashboard) return;
    const currentWidgets = currentEditDashboard.customReportComponents;

    dispatch(
      actionCreatorsReports.handleReportsChange(currentData => ({
        ...currentData,
        customReportComponents: {
          ...currentWidgets,
          [widget.id]: {
            ...currentWidgets[widget.id],
            ...widget,
          },
        },
      })),
    );
  };
};

export const removeCustomReportElements = (page?: number): AppThunk<void> => {
  return (dispatch, getState) => {
    const currentEditCustomReport = getState().reports.changeData
      .data as ICustomReport;
    if (!currentEditCustomReport) return;
    const numberOfRows = CUSTOM_REPORT_NUMBER_OF_ROWS;

    if (page) {
      const componentsToRemove: (number | string)[] = [];
      const newLayout = Object.entries(currentEditCustomReport.layout).reduce(
        (acc, [key, element]) => {
          const newElement = { ...element };
          const elementPage =
            element.y + element.h > numberOfRows
              ? Math.ceil((element.y + element.h) / numberOfRows)
              : 1;

          if (elementPage === page) {
            componentsToRemove.push(element.widgetId);
            delete acc[key];
            return acc;
          } else if (elementPage > page) {
            newElement.y = element.y - numberOfRows;
            return {
              ...acc,
              [key]: newElement,
            };
          } else
            return {
              ...acc,
              [key]: newElement,
            };
        },
        {} as ICustomReport['layout'],
      );

      const newComponents = Object.entries(
        currentEditCustomReport.customReportComponents,
      ).reduce(
        (acc, [key, element]) => {
          if (!element)
            return {
              ...acc,
              [key]: null,
            };
          if (componentsToRemove.includes(element.id)) {
            if (element.id.toString().includes('-')) {
              delete acc[element.id];
              return acc;
            } else {
              return {
                ...acc,
                [key]: null,
              };
            }
          }

          return {
            ...acc,
            [key]: element,
          };
        },
        {} as ICustomReport['customReportComponents'],
      );

      dispatch(
        actionCreatorsReports.handleReportsChange(currentData => ({
          ...currentData,
          layout: newLayout,
          customReportComponents: newComponents,
        })),
      );
    } else {
      dispatch(
        actionCreatorsReports.handleReportsChange(currentData => ({
          ...currentData,
          layout: {},
          customReportComponents: {},
        })),
      );
    }
  };
};

export const generateCustomReportFromDashboard = ({
  timePeriod,
  startDate,
  endDate,
}: IDashboardFilters): AppThunk<void> => {
  return async (dispatch, getState) => {
    const state = getState();
    const currentDashboard = selectCurrentDashboard(state);
    if (!currentDashboard) return;

    const newWidgetIds: {
      id: number | string;
      newWidgetId: number | string;
    }[] = [];

    dispatch(
      setIsLoadingModal({
        active: true,
        message: 'Generating custom report...',
      }),
    );
    const newComponents = Object.values(currentDashboard.widgets).reduce(
      (acc, widget) => {
        const newWidget = { ...widget } as ICustomReportComponent;
        newWidget.id = generateTabletGeneratedId();
        newWidgetIds.push({
          id: widget?.id ?? 0,
          newWidgetId: newWidget.id,
        });
        return {
          ...acc,
          [newWidget.id]: {
            ...newWidget,
            locationCustomReportId: 0,
            page: 0,
          },
        };
      },
      {} as Record<string, ICustomReportComponent>,
    );

    const newLayout = Object.values(currentDashboard.layout).reduce(
      (acc, element) => {
        const newElement = { ...element };
        const { isInCorrectPage, startingRow } =
          isCustomReportElementInCorrectPage(element);

        if (!isInCorrectPage) {
          newElement.y = startingRow;
        }
        return {
          ...acc,
          [element.id]: {
            ...newElement,
            widgetId:
              newWidgetIds.find(widget => widget.id === newElement.widgetId)
                ?.newWidgetId ?? newElement.widgetId,
          },
        };
      },
      {} as Record<string, IDashboardLayoutElement>,
    );

    const newData: TReportsStoreData = {
      id: 0,
      isActive: true,
      name: currentDashboard.name,
      isFavorited: false,
      period: timePeriod as EReportPeriod,
      startDate: startDate,
      endDate: endDate,
      categoryIds: null,
      subcategoryIds: null,
      orderTypes: null,
      reportingGroupIds: null,
      isSchedulingEnabled: false,
      fileType: EReportFileType.PDF,
      scheduleStartDateTime: '2025-04-25T21:44:16.417Z',
      repeatType: EReportRepeatType.DAILY,
      recipients: [],
      customReportId: 0,
      locationCustomReportId: 0,
      layout: newLayout,
      customReportComponents: newComponents,
    };

    router.navigate(getPathWithOrgData('reports/custom/add'));

    await new Promise(resolve => setTimeout(resolve, 2000));

    dispatch(
      actionCreatorsReports.handleReportsChange(currentData => ({
        ...currentData,
        ...newData,
      })),
    );
    dispatch(
      setIsLoadingModal({
        active: false,
      }),
    );
  };
};
